import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Sync as SyncIcon,
  SyncAlt as SyncAllIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Professional, ProfessionalService } from '../../types/professional';
import {
  getProfessionalsByAgent,
  createProfessional,
  updateProfessional,
  syncAllProfessionalsFromTrinks,
  syncOneProfessionalFromTrinks,
} from '../../utils/professionalApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface ProfessionalCadastrosDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  isTrinks: boolean;
}

type FormService = {
  externalId: string;
  name: string;
  durationMinutes: number;
  price: number;
  description: string;
  agendamentoUnico: boolean;
};

const emptyFormService = (): FormService => ({
  externalId: '',
  name: '',
  durationMinutes: 30,
  price: 0,
  description: '',
  agendamentoUnico: false,
});

function professionalToFormService(s: ProfessionalService): FormService {
  return {
    externalId: s.externalId ?? '',
    name: s.name ?? '',
    durationMinutes: s.durationMinutes ?? 30,
    price: typeof s.price === 'number' ? s.price : 0,
    description: s.description ?? '',
    agendamentoUnico: s.agendamentoUnico ?? false,
  };
}

const ProfessionalCadastrosDialog: React.FC<ProfessionalCadastrosDialogProps> = ({
  open,
  onClose,
  agentId,
  isTrinks,
}) => {
  const { t } = useTranslation();
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formProfessionalId, setFormProfessionalId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formSpecialization, setFormSpecialization] = useState('');
  const [formServices, setFormServices] = useState<FormService[]>([]);
  const [saving, setSaving] = useState(false);
  const theme = useTheme();

  const loadProfessionals = useCallback(async () => {
    if (!open || !agentId) return;
    setLoading(true);
    try {
      const data = await getProfessionalsByAgent(agentId);
      setProfessionals(data);
    } catch (err) {
      console.error(err);
      toast.error(t('agents.cadastros.loadError'));
    } finally {
      setLoading(false);
    }
  }, [open, agentId, t]);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      await syncAllProfessionalsFromTrinks(agentId);
      toast.success(t('agents.cadastros.syncAllSuccess'));
      await loadProfessionals();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : t('agents.cadastros.syncAllError'));
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncOne = async (professionalId: string) => {
    setSyncingId(professionalId);
    try {
      await syncOneProfessionalFromTrinks(agentId, professionalId);
      toast.success(t('agents.cadastros.syncOneSuccess'));
      await loadProfessionals();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : t('agents.cadastros.syncOneError'));
    } finally {
      setSyncingId(null);
    }
  };

  const openCreateForm = () => {
    setFormMode('create');
    setFormProfessionalId(null);
    setFormName('');
    setFormSpecialization('');
    setFormServices([emptyFormService()]);
    setFormOpen(true);
  };

  const openEditForm = (p: Professional) => {
    setFormMode('edit');
    setFormProfessionalId(p.id);
    setFormName(p.name);
    setFormSpecialization((p.specialization ?? []).join(', '));
    setFormServices(
      (p.services?.length ?? 0) > 0
        ? (p.services ?? []).map(professionalToFormService)
        : [emptyFormService()]
    );
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormProfessionalId(null);
    setFormName('');
    setFormSpecialization('');
    setFormServices([]);
  };

  const addFormService = () => {
    setFormServices((prev) => [...prev, emptyFormService()]);
  };

  const removeFormService = (index: number) => {
    setFormServices((prev) => (prev.length <= 1 ? [emptyFormService()] : prev.filter((_, i) => i !== index)));
  };

  const updateFormService = (index: number, field: keyof FormService, value: string | number | boolean) => {
    setFormServices((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const parseSpecialization = (s: string): string[] =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const handleSaveForm = async () => {
    const name = formName.trim();
    if (!name) {
      toast.error(t('agents.cadastros.name') + ' é obrigatório');
      return;
    }
    const specialization = parseSpecialization(formSpecialization);
    const services = formServices
      .map((s) => ({
        externalId: s.externalId || '',
        name: s.name.trim(),
        durationMinutes: Number(s.durationMinutes) || 0,
        price: Number(s.price) || 0,
        description: s.description.trim() || undefined,
        agendamentoUnico: s.agendamentoUnico ?? false,
      }))
      .filter((s) => s.name.length > 0);

    setSaving(true);
    try {
      if (formMode === 'create') {
        await createProfessional(agentId, {
          name,
          specialization,
          services,
        });
        toast.success(t('agents.cadastros.createSuccess'));
      } else if (formProfessionalId) {
        const existing = professionals.find((x) => x.id === formProfessionalId);
        await updateProfessional(formProfessionalId, {
          agentId: existing?.agentId,
          externalId: existing?.externalId,
          name,
          specialization,
          services,
        });
        toast.success(t('agents.cadastros.updateSuccess'));
      }
      await loadProfessionals();
      closeForm();
    } catch (err) {
      console.error(err);
      toast.error(
        err instanceof Error
          ? err.message
          : formMode === 'create'
            ? t('agents.cadastros.createError')
            : t('agents.cadastros.updateError')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('agents.cadastros.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" sx={{color: theme.palette.mode === 'dark' ? '#000' : undefined}} startIcon={<AddIcon />} onClick={openCreateForm}>
              {t('agents.cadastros.addProfessional')}
            </Button>
            {isTrinks && (
              <Button
                variant="outlined"
                startIcon={syncingAll ? <CircularProgress size={18} /> : <SyncAllIcon />}
                onClick={handleSyncAll}
                disabled={syncingAll}
              >
                {syncingAll ? t('agents.cadastros.syncing') : t('agents.cadastros.syncAll')}
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('agents.cadastros.name')}</TableCell>
                    <TableCell>{t('agents.cadastros.servicesCount')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {professionals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        {t('agents.cadastros.noProfessionals')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    professionals.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.services?.length ?? 0}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openEditForm(p)}
                            title={t('agents.cadastros.editProfessional')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {isTrinks && p.externalId && (
                            <IconButton
                              size="small"
                              onClick={() => handleSyncOne(p.id)}
                              disabled={!!syncingId}
                              title={t('agents.cadastros.syncOne')}
                            >
                              {syncingId === p.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SyncIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formMode === 'create'
            ? t('agents.cadastros.addProfessional')
            : t('agents.cadastros.editProfessional')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('agents.cadastros.name')}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('agents.cadastros.specialization')}
              value={formSpecialization}
              onChange={(e) => setFormSpecialization(e.target.value)}
              placeholder={t('agents.cadastros.specializationPlaceholder')}
              fullWidth
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('agents.cadastros.servicesCount')}
            </Typography>
            {formServices.map((s, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      #{index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeFormService(index)}
                      disabled={formServices.length <= 1}
                      title={t('agents.cadastros.removeService')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    size="small"
                    label={t('agents.cadastros.serviceName')}
                    value={s.name}
                    onChange={(e) => updateFormService(index, 'name', e.target.value)}
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label={t('agents.cadastros.durationMinutes')}
                      value={s.durationMinutes}
                      onChange={(e) =>
                        updateFormService(index, 'durationMinutes', parseInt(e.target.value, 10) || 0)
                      }
                      inputProps={{ min: 1 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t('agents.cadastros.price')}
                      value={s.price}
                      onChange={(e) =>
                        updateFormService(index, 'price', parseFloat(e.target.value) || 0)
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    label={t('agents.cadastros.description')}
                    value={s.description}
                    onChange={(e) => updateFormService(index, 'description', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={s.agendamentoUnico}
                        onChange={(e) => updateFormService(index, 'agendamentoUnico', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('agents.cadastros.agendamentoUnico')}
                  />
                </Box>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addFormService} variant="outlined" size="small">
              {t('agents.cadastros.addService')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>{t('common.cancel')}</Button>
          <Button sx={{color: theme.palette.mode === 'dark' ? '#000' : undefined}} variant="contained" onClick={handleSaveForm} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfessionalCadastrosDialog;
